import { useMutation } from "@apollo/react-hooks";
import _ from "lodash";
import omit from "lodash/omit";
import { useState } from "react";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    HtmlPreview,
    Input,
    LoadingProgress,
    Modal,
    Panel,
    Switch,
    Table,
    Text,
    Textarea,
    Thumbnail,
} from "@/ui";
import { useMeta, useNotification, useValidator } from "@/hooks";
import { formatUpdateOrderInput, getColorReservationMode } from "@/utils";
import { Medias } from "base/components/Medias";
import { useStoreContext } from "context";
import { MUTATION_UPDATE_STORE } from "../../apollo/module-operations/store";
import CheckoutFormAddItem from "./CheckoutFormAddItem";
import styles from "./GeneralsForm.module.scss";
import checkoutStyles from "./CheckoutForm.module.scss";
import {
    PlusIcon,
    RowActionsMenu,
    StoresProviderBadges,
    legacyColorToBadge,
    storeTableUiStyles,
} from "./shared/index";

const SaveIcon = () => <Icon icon="save" />;
const GlobeIcon = () => <i className="fas fa-globe" />;

/** DS shell for ACH banner URL — keeps legacy Medias picker logic. */
function AchBannerUrlField({ name, value, onChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const url = value ?? "";
    const canPreview = /^https?:\/\//i.test(url);

    const handleChangeMedia = nextUrl => {
        onChange({
            target: {
                name,
                value: nextUrl,
            },
        });
    };

    const openBrowse = event => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(true);
    };

    return (
        <div className={checkoutStyles.mediaField}>
            <Input
                fullWidth
                iconLeft={<GlobeIcon />}
                iconRight={
                    <button
                        className={checkoutStyles.browseInside}
                        onClick={openBrowse}
                        onMouseDown={event => {
                            // Keep focus from jumping to the input via <label>.
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        type="button"
                    >
                        Browse
                    </button>
                }
                label="ACH Payment Banner Url"
                name={name}
                onChange={onChange}
                value={url}
            />
            {canPreview ? (
                <Thumbnail
                    alt="ACH Payment Banner preview"
                    bordered
                    className={checkoutStyles.mediaPreview}
                    size="md"
                    src={url}
                    variant="product-image"
                />
            ) : null}
            <Medias
                defaultSelected={url}
                isOpen={isOpen}
                onChange={handleChangeMedia}
                toggle={() => setIsOpen(false)}
            />
        </div>
    );
}

const CheckoutForm = ({ inputs, setInputs, inputChange, loading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const { providers } = useStoreContext();
    const [form, setForm] = useState("");
    const [idSelected, setIdSelected] = useState(null);
    const [isCreate, setIsCreate] = useState(true);
    const [isLoadingUpdating, setIsLoadingUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [isloading, setIsLoading] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState({
        agreements: true,
        payments: true,
    });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "number_of_days_in_advance",
                method: "isEmpty",
                validWhen: false,
                message: "This number of days in advance is required.",
            },
            {
                field: "number_of_days_in_advance",
                method: value => {
                    const num = Number(value);
                    return !isNaN(num) && Number.isInteger(num);
                },
                validWhen: true,
                message: "Value must be an integer.",
            },
            {
                field: "number_of_days_in_advance",
                method: value => {
                    const num = Number(value);
                    return num >= 1 && num <= 30;
                },
                validWhen: true,
                message: "Value must be between 1 and 30.",
            },
        ],
        inputs,
    );

    const getColorPayment = mode => {
        if (mode === "ACH") {
            return "info";
        }

        if (mode === "CASH") {
            return "success";
        }

        return "metalic";
    };

    const toogle = (list, collapse) => {
        setIsCollapsed({
            ...isCollapsed,
            [list]: collapse,
        });
    };

    const [mutate] = useMutation(MUTATION_UPDATE_STORE, {
        refetchQueries: [],
        onCompleted: resp => {
            let input = _.cloneDeep(resp.updateStore);

            setInputs(input);
            notify(
                `${form} Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully`,
                "success",
            );
            handleClose();
        },
        onError: () => {
            setIsLoadingUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The ${form}, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const handlerUpdatingOrCreate = newArray => {
        setIsLoadingUpdating(true);
        let _inputs = {
            ...inputs,
            agreements: form === "Agreements" ? newArray : inputs.agreements,
            payment_settings:
                form === "Payments" ? newArray : inputs.payment_settings,
        };

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput({
                    ...omit(_inputs, "ignore_whitespace"),
                }),
            },
        });
    };

    const createOrUpdate = () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            setIsLoading(true);
            handlerUpdatingOrCreate();
        }
    };

    const renderFormCreate = nextForm => {
        setForm(nextForm);
        setIsCreate(true);
        window.scrollTo(0, 0);
    };

    const handleClose = () => {
        setIsLoadingUpdating(false);
        setIsDelete(false);
        setIsLoading(false);
        setShowDeleteConfirm(false);
        setForm("");
    };

    const handleCancel = () => {
        setInputs(inputs);
        handleClose();
    };

    const handleDelete = list => {
        return list.filter((_, idx) => idx !== idSelected);
    };

    const launchDeleteConfirm = () => {
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        let _inputs = {
            ...inputs,
            agreements:
                form === "Agreements"
                    ? handleDelete(inputs.agreements)
                    : inputs.agreements,
            payment_settings:
                form === "Payments"
                    ? handleDelete(inputs.payment_settings)
                    : inputs.payment_settings,
        };

        setIsLoadingUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput({
                    ...omit(_inputs, [
                        "display_post_tax",
                        "show_reward_at_checkout",
                        "debit_card_fee",
                        "ach_payment_banner_url",
                        "warning_setting",
                        "ignore_whitespace",
                    ]),
                }),
            },
        });
    };

    const _agreements = ((inputs && inputs.agreements) || []).map(
        (item, index) => ({
            ...item,
            _index: index,
        }),
    );
    const _payments = ((inputs && inputs.payment_settings) || []).map(
        (item, index) => ({
            ...item,
            _index: index,
        }),
    );

    const agreementActionsColumn = {
        id: "actions",
        header: "",
        width: 52,
        align: "center",
        renderCell: row => (
            <span
                className={storeTableUiStyles.rowActions}
                onClick={event => event.stopPropagation()}
            >
                <RowActionsMenu
                    disabled={form !== "" || isLoadingUpdating}
                    label={row.reservation_mode || "agreement"}
                    onDelete={() => {
                        setIdSelected(row._index);
                        setForm("Agreements");
                        setIsDelete(true);
                        setIsCreate(false);
                        launchDeleteConfirm();
                    }}
                    onUpdate={() => {
                        setIsCreate(false);
                        setIdSelected(row._index);
                        setForm("Agreements");
                        window.scrollTo(0, 0);
                    }}
                />
            </span>
        ),
    };

    const agreementColumns = [
        agreementActionsColumn,
        {
            id: "reservation_mode",
            header: "Reservation Mode",
            minWidth: 160,
            renderCell: row => (
                <Badge
                    variant={legacyColorToBadge(
                        getColorReservationMode(row.reservation_mode) || "info",
                    )}
                >
                    {row.reservation_mode}
                </Badge>
            ),
        },
        {
            id: "content",
            header: "Content",
            minWidth: 200,
            renderCell: row => `${row.content || ""}`,
        },
        {
            id: "check_by_default",
            header: "Check By Default",
            minWidth: 140,
            renderCell: row => (
                <Badge
                    variant={legacyColorToBadge(
                        row.check_by_default ? "success" : "error",
                    )}
                >
                    {row.check_by_default ? "Yes" : "No"}
                </Badge>
            ),
        },
        {
            id: "required",
            header: "Required",
            minWidth: 100,
            renderCell: row => (
                <Badge
                    variant={legacyColorToBadge(
                        row.required ? "success" : "error",
                    )}
                >
                    {row.required ? "Yes" : "No"}
                </Badge>
            ),
        },
        {
            id: "stores_provider_ids",
            header: "Stores Provider",
            minWidth: 200,
            renderCell: row => (
                <StoresProviderBadges value={row.stores_provider_ids} />
            ),
        },
    ];

    const paymentColumns = [
        {
            id: "actions",
            header: "",
            width: 52,
            align: "center",
            renderCell: row => (
                <span
                    className={storeTableUiStyles.rowActions}
                    onClick={event => event.stopPropagation()}
                >
                    <RowActionsMenu
                        disabled={form !== "" || isLoadingUpdating}
                        label={row.payment_type || "payment"}
                        onDelete={() => {
                            setIdSelected(row._index);
                            setForm("Payments");
                            setIsDelete(true);
                            setIsCreate(false);
                            launchDeleteConfirm();
                        }}
                        onUpdate={() => {
                            setIsCreate(false);
                            setIdSelected(row._index);
                            setForm("Payments");
                            window.scrollTo(0, 0);
                        }}
                    />
                </span>
            ),
        },
        {
            id: "reservation_mode",
            header: "Reservation Mode",
            minWidth: 160,
            renderCell: row => (
                <Badge
                    variant={legacyColorToBadge(
                        getColorReservationMode(row.reservation_mode) || "info",
                    )}
                >
                    {row.reservation_mode}
                </Badge>
            ),
        },
        {
            id: "payment_type",
            header: "Payment Type",
            minWidth: 140,
            renderCell: row => (
                <Badge
                    variant={legacyColorToBadge(
                        getColorPayment(row.payment_type) || "info",
                    )}
                >
                    {row.payment_type}
                </Badge>
            ),
        },
        {
            id: "stores_provider_ids",
            header: "Stores Provider",
            minWidth: 200,
            renderCell: row => (
                <StoresProviderBadges value={row.stores_provider_ids} />
            ),
        },
    ];

    return (
        <>
            <Modal
                description={`Are you sure you want to remove the item from ${form} ?`}
                footer={
                    <div className={checkoutStyles.modalActions}>
                        <Button
                            loading={isLoadingUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isLoadingUpdating}
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setIsDelete(false);
                            }}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) {
                        setShowDeleteConfirm(false);
                        setIsDelete(false);
                    }
                }}
                open={showDeleteConfirm}
                title="Remove checkout item"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={checkoutStyles.headerText}>
                            <Heading level={4}>Checkout</Heading>
                            <Text tone="secondary" variant="body-md">
                                Checkout Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "add-item",
                                    label: "Add Item",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: form !== "" || isLoadingUpdating,
                                    menuItems: [
                                        {
                                            id: "agreements",
                                            label: "Agreement",
                                            icon: <PlusIcon />,
                                            onSelect: () =>
                                                renderFormCreate("Agreements"),
                                        },
                                        {
                                            id: "payments",
                                            label: "Payment",
                                            icon: <PlusIcon />,
                                            onSelect: () =>
                                                renderFormCreate("Payments"),
                                        },
                                    ],
                                },
                                {
                                    id: "update",
                                    label: "Update",
                                    icon: isloading ? undefined : <SaveIcon />,
                                    loading: isloading,
                                    disabled: isloading || form !== "",
                                    onClick: createOrUpdate,
                                },
                            ]}
                            aria-label="Checkout actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {loading ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            indeterminate
                            indicator="circular"
                            size="sm"
                            title="Loading"
                        />
                    </div>
                ) : (
                    <>
                        <FormLayout>
                            <FormLayout.Section title="Information">
                                <Switch
                                    checked={Boolean(
                                        inputs.show_reward_at_checkout,
                                    )}
                                    label="Show Reward At Checkout"
                                    name="show_reward_at_checkout"
                                    onChange={inputChange}
                                />
                                <Switch
                                    checked={Boolean(
                                        inputs.disabled_time_window,
                                    )}
                                    label="Disabled Time Window"
                                    name="disabled_time_window"
                                    onChange={inputChange}
                                />
                                <FormLayout.Grid
                                    columns={2}
                                    maxWidth="twoThirds"
                                >
                                    <FormLayout.Full>
                                        <Input
                                            error={
                                                validator
                                                    .number_of_days_in_advance
                                                    .isInvalid
                                                    ? validator
                                                          .number_of_days_in_advance
                                                          .message
                                                    : undefined
                                            }
                                            fullWidth
                                            label="Allow number of days in advance"
                                            max={10}
                                            min={1}
                                            name="number_of_days_in_advance"
                                            onChange={inputChange}
                                            step={1}
                                            type="number"
                                            value={
                                                inputs.number_of_days_in_advance ||
                                                ""
                                            }
                                        />
                                    </FormLayout.Full>
                                </FormLayout.Grid>
                                <FormLayout.Grid
                                    columns={2}
                                    maxWidth="twoThirds"
                                >
                                    <FormLayout.Full>
                                        <AchBannerUrlField
                                            fullWidth
                                            name="ach_payment_banner_url"
                                            onChange={inputChange}
                                            value={
                                                inputs.ach_payment_banner_url ??
                                                ""
                                            }
                                        />
                                    </FormLayout.Full>
                                </FormLayout.Grid>
                                <FormLayout.Grid
                                    columns={2}
                                    maxWidth="twoThirds"
                                >
                                    {inputs.warning_setting ? (
                                        <>
                                            <Textarea
                                                fullWidth
                                                label="Warnings"
                                                name="warning_setting"
                                                onChange={inputChange}
                                                placeholder="Write the warnings..."
                                                rows={12}
                                                value={inputs.warning_setting}
                                            />
                                            <HtmlPreview
                                                html={inputs.warning_setting}
                                                title="Warnings Preview"
                                            />
                                        </>
                                    ) : (
                                        <FormLayout.Full>
                                            <Textarea
                                                fullWidth
                                                label="Warnings"
                                                name="warning_setting"
                                                onChange={inputChange}
                                                placeholder="Write the warnings..."
                                                rows={12}
                                                value=""
                                            />
                                        </FormLayout.Full>
                                    )}
                                </FormLayout.Grid>
                            </FormLayout.Section>
                        </FormLayout>

                        {form !== "" && !isDelete ? (
                            <CheckoutFormAddItem
                                form={form}
                                handleCancel={handleCancel}
                                handlerUpdatingOrCreate={
                                    handlerUpdatingOrCreate
                                }
                                idSelected={idSelected}
                                isCreate={isCreate}
                                isLoadingUpdating={isLoadingUpdating}
                                store={inputs}
                                stores={providers}
                            />
                        ) : null}

                        <div className={checkoutStyles.listSection}>
                            <button
                                className={checkoutStyles.listCollapse}
                                onClick={() =>
                                    toogle(
                                        "agreements",
                                        !isCollapsed.agreements,
                                    )
                                }
                                type="button"
                            >
                                <Heading
                                    className={checkoutStyles.listCollapseTitle}
                                    level={5}
                                >
                                    Agreements
                                </Heading>
                                <i
                                    className={`fas fa-caret-${
                                        isCollapsed.agreements ? "up" : "down"
                                    }`}
                                />
                            </button>
                            {isCollapsed.agreements ? (
                                <div className={checkoutStyles.tableWrap}>
                                    <Table
                                        columns={agreementColumns}
                                        getRowId={row => String(row._index)}
                                        rows={_agreements}
                                    />
                                </div>
                            ) : null}
                        </div>

                        <div className={checkoutStyles.listSection}>
                            <button
                                className={checkoutStyles.listCollapse}
                                onClick={() =>
                                    toogle("payments", !isCollapsed.payments)
                                }
                                type="button"
                            >
                                <Heading
                                    className={checkoutStyles.listCollapseTitle}
                                    level={5}
                                >
                                    Payment Setting
                                </Heading>
                                <i
                                    className={`fas fa-caret-${
                                        isCollapsed.payments ? "up" : "down"
                                    }`}
                                />
                            </button>
                            {isCollapsed.payments ? (
                                <div className={checkoutStyles.tableWrap}>
                                    <Table
                                        columns={paymentColumns}
                                        getRowId={row => String(row._index)}
                                        rows={_payments}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </>
                )}
            </Panel>
        </>
    );
};

export default CheckoutForm;
